import { Fragment, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AuditEntry, page } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useListState } from '../../lib/useList';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Button, EmptyRow, FilterBar, PageHeader, Pager, Spinner, TableWrap, TextField, Th } from '../../components/ui';

const AuditPageSchema = page(AuditEntry);
type Entry = typeof AuditEntry._output;

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function show(v: unknown): string {
  if (v === undefined) return '';
  return typeof v === 'string' ? v : JSON.stringify(v);
}

/** Before/after view: one row per changed field. */
export function AuditDiff({ before, after }: { before: Entry['before']; after: Entry['after'] }) {
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].sort();
  if (keys.length === 0) return <p className="text-sm text-slate-600">{t('audit.noDetails')}</p>;
  return (
    <table className="w-full text-sm">
      <caption className="sr-only">{t('audit.changes')}</caption>
      <thead>
        <tr>
          <Th>{t('audit.field')}</Th>
          <Th>{t('audit.before')}</Th>
          <Th>{t('audit.after')}</Th>
        </tr>
      </thead>
      <tbody>
        {keys.map((k) => {
          const b = before?.[k];
          const a = after?.[k];
          return (
            <tr key={k} className="border-t border-slate-100 hover:bg-slate-50 align-top">
              <th scope="row" className="px-3 py-1 text-left font-mono font-normal">
                {k}
              </th>
              <td className="bg-red-50 px-3 py-1 font-mono">{isRecord(b) ? JSON.stringify(b) : show(b)}</td>
              <td className="bg-green-50 px-3 py-1 font-mono">{isRecord(a) ? JSON.stringify(a) : show(a)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function AuditPage({ platform = false }: { platform?: boolean }) {
  const list = useListState();
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const base = platform ? '/platform/audit' : '/audit';
  const reset = list.resetPage;
  const q = useQuery({
    queryKey: ['audit', base, { page: list.page, search: list.search, action, entityType, tenantId, from, to }],
    queryFn: () =>
      api.get(base, {
        query: {
          page: list.page,
          pageSize: 25,
          search: list.search,
          action,
          entityType,
          ...(platform ? { tenantId } : {}),
          from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
          to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
          order: 'desc',
        },
        schema: AuditPageSchema,
      }),
    placeholderData: keepPreviousData,
  });
  const onFilter = (set: (v: string) => void) => (e: { target: { value: string } }) => {
    set(e.target.value);
    reset();
  };
  const cols = 5;
  return (
    <>
      <PageHeader title={platform ? t('nav.platform.audit') : t('nav.audit')} description={platform ? t('nav.platform.audit.desc') : t('nav.audit.desc')} />
      <FilterBar columns="sm:grid-cols-2 lg:grid-cols-3">
        <TextField label={t('common.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <TextField label={t('audit.action')} value={action} onChange={onFilter(setAction)} />
        <TextField label={t('audit.entity')} value={entityType} onChange={onFilter(setEntityType)} />
        {platform ? <TextField label={t('audit.tenantId')} value={tenantId} onChange={onFilter(setTenantId)} /> : null}
        <TextField label={t('audit.from')} type="date" value={from} onChange={onFilter(setFrom)} />
        <TextField label={t('audit.to')} type="date" value={to} onChange={onFilter(setTo)} />
      </FilterBar>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={platform ? t('nav.platform.audit') : t('nav.audit')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('audit.when')}</Th>
                <Th>{t('audit.who')}</Th>
                <Th>{t('audit.action')}</Th>
                <Th>{t('audit.entity')}</Th>
                <Th>{t('common.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? <EmptyRow cols={cols} /> : null}
              {q.data.items.map((e) => (
                <Fragment key={e.id}>
                  <tr className="border-t border-slate-100 hover:bg-slate-50 align-top">
                    <td className="px-4 py-3">{formatDateTime(e.at)}</td>
                    <td className="px-4 py-3">{e.actorLabel}</td>
                    <td className="px-4 py-3 font-mono">{e.action}</td>
                    <td className="px-4 py-3">
                      {e.entityType}
                      {e.entityId ? <span className="block break-all font-mono text-xs text-slate-600">{e.entityId}</span> : null}
                    </td>
                    <td className="px-4 py-3">
                      <Button aria-expanded={open === e.id} onClick={() => setOpen(open === e.id ? null : e.id)}>
                        {open === e.id ? t('audit.hideDetails') : t('audit.showDetails')}
                      </Button>
                    </td>
                  </tr>
                  {open === e.id ? (
                    <tr className="bg-slate-50">
                      <td colSpan={cols} className="px-4 py-3">
                        <AuditDiff before={e.before} after={e.after} />
                        <p className="mt-2 text-xs text-slate-600">
                          {t('audit.ip')}: {e.ip ?? '-'} {e.requestId ? `(${e.requestId})` : ''}
                        </p>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
